import { Link } from "@tanstack/react-router";
import { Eye, EyeOff, LogOut } from "lucide-react";
import { useRef, type RefObject } from "react";
import { Sheet } from "../ui/sheet";
import { ThemeToggle } from "../theme-toggle";
import {
  isMobileNavigationSectionActive,
  mobileNavigationFor,
} from "../../lib/navigation";
import { cn } from "../../lib/utils";
import type { AuthUser } from "../../stores/auth";

interface MobileNavigationSheetProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  initialSection: "navigation" | "account";
  user: AuthUser;
  pathname: string;
  hideHeaderIdentity: boolean;
  identityPreferenceBusy: boolean;
  onToggleIdentity(): void;
  onLogout(): void;
  restoreFocusRef?: RefObject<HTMLElement>;
}

export function MobileNavigationSheet({
  open,
  onOpenChange,
  initialSection,
  user,
  pathname,
  hideHeaderIdentity,
  identityPreferenceBusy,
  onToggleIdentity,
  onLogout,
  restoreFocusRef,
}: MobileNavigationSheetProps) {
  const navigation = mobileNavigationFor(user);
  const firstAccountActionRef = useRef<HTMLButtonElement>(null);

  const close = () => onOpenChange(false);
  const logout = () => {
    close();
    onLogout();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Navigation and account"
      description="Cascena destinations permitted for your current role and modules."
      initialFocusRef={
        initialSection === "account" ? firstAccountActionRef : undefined
      }
      restoreFocusRef={restoreFocusRef}
    >
      <nav aria-label="Mobile navigation" className="space-y-5">
        <div>
          <MobileNavigationLink
            to="/"
            active={pathname === "/"}
            onNavigate={close}
          >
            Dashboard
          </MobileNavigationLink>
        </div>

        {navigation.map((section) => (
          <section key={section.id} aria-labelledby={`mobile-nav-${section.id}`}>
            <div className="mb-1.5 flex min-h-8 items-center justify-between gap-3 px-3">
              <h2
                id={`mobile-nav-${section.id}`}
                data-nonessential-decoration
                className="text-xs font-semibold uppercase tracking-[0.12em] text-muted"
              >
                {section.mobileLabel}
              </h2>
              {isMobileNavigationSectionActive(section.id, pathname) && (
                <span
                  data-nonessential-decoration
                  className="text-xs font-medium uppercase tracking-wide text-accent"
                >
                  Current
                </span>
              )}
            </div>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <MobileNavigationLink
                  key={item.to}
                  to={item.to}
                  active={pathname === item.to}
                  onNavigate={close}
                >
                  {item.label.replace(" ▸", "")}
                </MobileNavigationLink>
              ))}
            </div>
          </section>
        ))}

        {navigation.length === 0 && (
          <p className="rounded border border-border/70 bg-border/10 px-3 py-3 text-sm text-muted">
            No business modules are enabled for this account.
          </p>
        )}
      </nav>

      <section
        aria-labelledby="mobile-account-heading"
        className="border-t border-border/70 pt-5"
      >
        <h2
          id="mobile-account-heading"
          data-nonessential-decoration
          className="px-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted"
        >
          Account
        </h2>
        <div className="mt-3 rounded border border-border/70 bg-border/10 px-3 py-3">
          <div className="break-all text-sm font-medium text-fg">
            {hideHeaderIdentity ? user.role : user.email}
          </div>
          {!hideHeaderIdentity && (
            <div className="mt-1 break-words text-sm text-muted">
              {user.companyName ?? "platform"} · {user.role}
            </div>
          )}
        </div>

        <div className="mt-3 space-y-1">
          <button
            ref={firstAccountActionRef}
            type="button"
            onClick={onToggleIdentity}
            disabled={identityPreferenceBusy}
            className={cn(
              "mobile-sheet-action flex w-full items-center gap-3 rounded px-3 text-left text-sm",
              "text-muted transition-colors hover:bg-border/25 hover:text-fg",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
            )}
          >
            {user.hideIdentityInHeader ? (
              <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : (
              <EyeOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            <span>
              {user.hideIdentityInHeader
                ? "Show email and company"
                : "Hide email and company"}
            </span>
          </button>

          <div className="mobile-sheet-theme flex min-h-11 items-center justify-between gap-3 px-3 text-sm text-muted">
            <span>Appearance</span>
            <ThemeToggle />
          </div>

          <button
            type="button"
            onClick={logout}
            className="mobile-sheet-action flex w-full items-center gap-3 rounded px-3 text-left text-sm text-muted transition-colors hover:bg-border/25 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Log out</span>
          </button>
        </div>
      </section>
    </Sheet>
  );
}

function MobileNavigationLink({
  to,
  active,
  onNavigate,
  children,
}: {
  to: string;
  active: boolean;
  onNavigate(): void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      replace
      onClick={(event) => {
        // The sheet owns a temporary same-URL history entry so browser Back can
        // close it. Do not let an already-current link replace that marker with
        // another same-URL entry; closing the sheet will pop the marker instead.
        if (active) event.preventDefault();
        onNavigate();
      }}
      aria-current={active ? "page" : undefined}
      className={cn(
        "mobile-navigation-link flex items-center rounded px-3 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        active
          ? "bg-accent/10 font-medium text-fg"
          : "text-muted hover:bg-border/25 hover:text-fg"
      )}
    >
      {children}
    </Link>
  );
}
